import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { PaymentData } from '../components/Forms/ReviewForm/types'
import { PaymentProfileHsaCard } from '../generated/hooks'
import { ReinstatementPlanName } from '../utils/reinstatementUtils'

const PAYMENT_DATA_STORAGE_KEY = 'payment_data_v1'
const REINSTATEMENT_DATA_STORAGE_KEY = 'reinstatement_data_v1'

type AutoPayStatusType = {
  autopayStatus: 'success' | 'failed' | 'not-requested'
}

type ReinstatementPaymentPlanType = {
  name: ReinstatementPlanName
  amount: number
  frequency: '12 months' | '6 months' | '3 months'
  installments: number
  monthsPerInstallment: number
}

// Persisted reinstatement session state — primitives only. Anything derivable
// (plan amounts, frequency, installments) is recomputed at render time from
// `msrp` + `selectedPlanName`, never stored, so the persisted shape can never
// drift from the displayed values.
type ReinstatementData = {
  msrp: number
  selectedPlanName: ReinstatementPlanName
  nonAcceptedPlans: ReinstatementPlanName[]
}

export function computeReinstatementPlans(msrp: number): ReinstatementPaymentPlanType[] {
  return [
    { name: 'Annually', amount: parseFloat(msrp.toFixed(2)), frequency: '12 months', installments: 1, monthsPerInstallment: 12 },
    { name: 'Semi-Annually', amount: parseFloat((msrp / 2).toFixed(2)), frequency: '6 months', installments: 2, monthsPerInstallment: 6 },
    { name: 'Quarterly', amount: parseFloat((msrp / 4).toFixed(2)), frequency: '3 months', installments: 4, monthsPerInstallment: 3 },
  ]
}

// Annually is always present in computeReinstatementPlans output, so the non-null
// assertion is safe. Centralizing the lookup keeps the invariant in one place.
export const findAnnualPlan = (plans: ReinstatementPaymentPlanType[]): ReinstatementPaymentPlanType =>
  plans.find((p) => p.name === 'Annually')!

// Fail-loud sentinel. Reinstatement users can't reach /payment without a real msrp
// (useAuthenticate.tsx blocks at /verify on contact-info failure or null msrp), so
// $0.00 plan amounts only render if something has gone genuinely wrong.
const DEFAULT_MSRP = 0

const defaultReinstatementData = (): ReinstatementData => ({
  msrp: DEFAULT_MSRP,
  selectedPlanName: 'Annually',
  nonAcceptedPlans: [],
})

const PLAN_NAMES: ReinstatementPlanName[] = ['Annually', 'Semi-Annually', 'Quarterly']
const isPlanName = (v: unknown): v is ReinstatementPlanName =>
  typeof v === 'string' && (PLAN_NAMES as string[]).includes(v)

const hydrateReinstatementData = (): ReinstatementData => {
  const fallback = defaultReinstatementData()
  try {
    const raw = sessionStorage.getItem(REINSTATEMENT_DATA_STORAGE_KEY)
    if (raw === null) return fallback
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return fallback

    const partial = parsed as Partial<Record<keyof ReinstatementData, unknown>>
    return {
      msrp: typeof partial.msrp === 'number' && Number.isFinite(partial.msrp) ? partial.msrp : fallback.msrp,
      selectedPlanName: isPlanName(partial.selectedPlanName) ? partial.selectedPlanName : fallback.selectedPlanName,
      nonAcceptedPlans: Array.isArray(partial.nonAcceptedPlans)
        ? partial.nonAcceptedPlans.filter(isPlanName)
        : fallback.nonAcceptedPlans,
    }
  } catch {
    return fallback
  }
}

type PaymentDataType = Omit<PaymentData, 'autoPay'> & AutoPayStatusType

const defaultPaymentData: PaymentDataType = {
  amount: '',
  paymentMethod: '',
  HSACard__c: PaymentProfileHsaCard.No,
  Address: '',
  Address2: '',
  City: '',
  State: '',
  BillingZipCode: '',
  contact_id: '',
  type: 'Bank',
  scheduled: new Date(),
  ACHInfo: null,
  CreditCardInfo: null,
  transactionId: '',
  transactionName: '',
  autopayStatus: 'not-requested',
}

const PaymentDataContext = createContext<{
  paymentData: PaymentDataType
  clearPaymentData: () => void
  submitPaymentData: (next: Partial<PaymentDataType>) => void
  selectedReinstatementPaymentPlan: ReinstatementPaymentPlanType
  handleSelectReinstatementPaymentPlan: (plan: ReinstatementPaymentPlanType) => void
  msrp: number
  setMsrp: (msrp: number) => void
  availablePlans: ReinstatementPaymentPlanType[]
  setNonAcceptedPlans: (plans: ReinstatementPlanName[]) => void
}>({
  paymentData: defaultPaymentData,
  clearPaymentData: () => {},
  submitPaymentData: () => {},
  selectedReinstatementPaymentPlan: findAnnualPlan(computeReinstatementPlans(DEFAULT_MSRP)),
  handleSelectReinstatementPaymentPlan: () => {},
  msrp: DEFAULT_MSRP,
  setMsrp: () => {},
  availablePlans: computeReinstatementPlans(DEFAULT_MSRP),
  setNonAcceptedPlans: () => {},
})

export const PaymentDataProvider = ({ children }: { children: React.ReactNode }) => {
  const [paymentData, setPaymentData] = useState<PaymentDataType>(() => {
    try {
      const raw = sessionStorage.getItem(PAYMENT_DATA_STORAGE_KEY)
      if (raw) {
        const parsed = JSON.parse(raw) as PaymentDataType
        // Convert scheduled back to Date object if it's a string
        if (parsed.scheduled && typeof parsed.scheduled === 'string') {
          parsed.scheduled = new Date(parsed.scheduled)
        }
        return parsed
      }
      return defaultPaymentData
    } catch {
      return defaultPaymentData
    }
  })
  const [reinstatementData, setReinstatementData] = useState<ReinstatementData>(() => hydrateReinstatementData())

  const { msrp, selectedPlanName, nonAcceptedPlans } = reinstatementData

  const availablePlans = useMemo(
    () => computeReinstatementPlans(msrp).filter((p) => !nonAcceptedPlans.includes(p.name)),
    [msrp, nonAcceptedPlans],
  )

  // The exposed plan object is derived on every render from the current msrp +
  // selectedPlanName. Falls back to the unfiltered list if the selected name was
  // filtered out — the re-sync effect below catches up the persisted state, but
  // until it runs we must still return a real plan object.
  const selectedReinstatementPaymentPlan = useMemo<ReinstatementPaymentPlanType>(() => {
    const allPlans = computeReinstatementPlans(msrp)
    return allPlans.find((p) => p.name === selectedPlanName) ?? findAnnualPlan(allPlans)
  }, [msrp, selectedPlanName])

  // Single point for state-change-with-persist: writes storage atomically alongside
  // each functional update. Storage writes are idempotent, so StrictMode's double-
  // invocation of the updater is harmless. Persist-on-mount is intentionally avoided
  // — initial state (default or hydrated) is never written back to storage just from
  // mounting, so a manual `clear` in DevTools stays cleared until something actually
  // changes state.
  const updateReinstatementData = useCallback((updater: (prev: ReinstatementData) => ReinstatementData) => {
    setReinstatementData((prev) => {
      const next = updater(prev)
      sessionStorage.setItem(REINSTATEMENT_DATA_STORAGE_KEY, JSON.stringify(next))
      return next
    })
  }, [])

  // If the selected plan name is no longer in availablePlans (physician now excludes
  // it), fall back to Annually if available, else the first available. No amount-drift
  // check needed — amount is always derived from msrp, never stored.
  useEffect(() => {
    if (availablePlans.length === 0) return
    if (availablePlans.some((p) => p.name === selectedPlanName)) return
    const fallback = availablePlans.find((p) => p.name === 'Annually') ?? availablePlans[0]
    updateReinstatementData((prev) => ({ ...prev, selectedPlanName: fallback.name }))
  }, [availablePlans, selectedPlanName, updateReinstatementData])

  const setMsrp = useCallback(
    (value: number) => updateReinstatementData((prev) => ({ ...prev, msrp: value })),
    [updateReinstatementData],
  )

  const setNonAcceptedPlans = useCallback(
    (plans: ReinstatementPlanName[]) => updateReinstatementData((prev) => ({ ...prev, nonAcceptedPlans: plans })),
    [updateReinstatementData],
  )

  const handleSelectReinstatementPaymentPlan = useCallback(
    (plan: ReinstatementPaymentPlanType) => updateReinstatementData((prev) => ({ ...prev, selectedPlanName: plan.name })),
    [updateReinstatementData],
  )

  const clearPaymentData = useCallback(() => {
    sessionStorage.removeItem(PAYMENT_DATA_STORAGE_KEY)
    sessionStorage.removeItem(REINSTATEMENT_DATA_STORAGE_KEY)
    setPaymentData(defaultPaymentData)
    setReinstatementData(defaultReinstatementData())
  }, [])

  const submitPaymentData = useCallback(
    (next: Partial<PaymentDataType>) => {
      const merged = { ...paymentData, ...next }
      // Serialize Date objects to ISO strings for sessionStorage
      const serialized = {
        ...merged,
        scheduled: merged.scheduled instanceof Date ? merged.scheduled.toISOString() : merged.scheduled,
      }
      sessionStorage.setItem(PAYMENT_DATA_STORAGE_KEY, JSON.stringify(serialized))
      setPaymentData((prev) => ({ ...prev, ...next }))
    },
    [paymentData],
  )

  const value = useMemo(
    () => ({
      paymentData,
      clearPaymentData,
      submitPaymentData,
      selectedReinstatementPaymentPlan,
      handleSelectReinstatementPaymentPlan,
      msrp,
      setMsrp,
      availablePlans,
      setNonAcceptedPlans,
    }),
    [
      paymentData,
      clearPaymentData,
      submitPaymentData,
      selectedReinstatementPaymentPlan,
      handleSelectReinstatementPaymentPlan,
      msrp,
      setMsrp,
      availablePlans,
      setNonAcceptedPlans,
    ],
  )

  return <PaymentDataContext.Provider value={value}>{children}</PaymentDataContext.Provider>
}

export default function usePaymentDataContext() {
  return useContext(PaymentDataContext)
}
